/**
 * Browser/device info collection utility
 * Collects properties similar to what Mixpanel auto-collected
 * Uses lazy evaluation and caching for static properties
 */

import type {
  BrowserName,
  BrowserProperties,
  DeviceName,
  OSName,
  StaticBrowserInfo
} from '@/types/analytics';

// Cache for static browser properties (don't change during session)
let cachedStaticInfo: StaticBrowserInfo | null = null;

const isIOS = (): boolean => {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) return true;
  // iPadOS 13+ defaults Safari to desktop mode, which drops the iPad token
  // and reports a Macintosh UA. Touch points are what tell it from a real Mac.
  return ua.includes('Macintosh') && navigator.maxTouchPoints > 1;
};

// Parse user agent to extract OS info
const getOS = (): OSName => {
  const ua = navigator.userAgent;
  // Check Chrome OS before Linux (Chrome OS UA includes "Linux")
  if (ua.includes('CrOS')) return 'Chrome OS';
  if (ua.includes('Win')) return 'Windows';
  // Check iOS before Mac OS X: every iOS UA contains "like Mac OS X".
  if (isIOS()) return 'iOS';
  if (ua.includes('Mac')) return 'Mac OS X';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('Linux')) return 'Linux';
  return 'Unknown';
};

// Mobile device model, omitted on desktop — mirrors the Mixpanel JS lib.
const getDevice = (): DeviceName | null => {
  const ua = navigator.userAgent;
  if (ua.includes('iPhone')) return 'iPhone';
  if (ua.includes('iPod')) return 'iPod Touch';
  if (ua.includes('iPad') || isIOS()) return 'iPad';
  if (ua.includes('Android')) return 'Android';
  return null;
};

const isMobile = (): boolean => {
  const ua = navigator.userAgent;
  // isIOS covers iPadOS desktop mode, whose UA carries no mobile token.
  return /Mobile|Android|iPhone|iPad|iPod/i.test(ua) || isIOS();
};

const getBrowser = (): BrowserName => {
  const ua = navigator.userAgent;
  const mobile = isMobile();

  // Order matters - check more specific patterns first

  // Headless Chrome (bots/automation)
  if (ua.includes('HeadlessChrome')) return 'HeadlessChrome';

  // In-app browsers
  if (ua.includes('FBAN') || ua.includes('FBAV')) return 'Facebook';

  // Samsung Internet
  if (ua.includes('SamsungBrowser')) return 'Samsung Internet';

  // Xiaomi's browser
  if (ua.includes('MiuiBrowser')) return 'MiuiBrowser';

  // Yandex
  if (ua.includes('YaBrowser')) return 'Yandex Browser';

  // Edge (check before Chrome since Edge includes Chrome in UA)
  if (ua.includes('Edg/') || ua.includes('EdgA/') || ua.includes('EdgiOS/')) {
    return mobile ? 'Edge Mobile' : 'Edge';
  }

  // Opera (check before Chrome since Opera includes Chrome in UA)
  if (ua.includes('OPR/') || ua.includes('Opera/') || ua.includes('OPiOS/')) {
    return mobile ? 'Opera Mobile' : 'Opera';
  }

  // Firefox
  if (ua.includes('Firefox/') || ua.includes('FxiOS/')) {
    return mobile ? 'Firefox Mobile' : 'Firefox';
  }

  // Chrome (check before Safari since Chrome includes Safari in UA)
  if (ua.includes('Chrome/') || ua.includes('CriOS/')) {
    return mobile ? 'Chrome Mobile' : 'Chrome';
  }

  // Safari (must be last of the major browsers)
  if (ua.includes('Safari/')) {
    return mobile ? 'Mobile Safari' : 'Safari';
  }

  return 'Unknown';
};

const getBrowserVersion = (): number | null => {
  const ua = navigator.userAgent;
  const browser = getBrowser();

  const versionPatterns: Record<BrowserName, RegExp | null> = {
    Chrome: /Chrome\/(\d+)/,
    'Chrome Mobile': /(?:Chrome|CriOS)\/(\d+)/,
    Edge: /Edg\/(\d+)/,
    'Edge Mobile': /(?:EdgA|EdgiOS|Edg)\/(\d+)/,
    Facebook: /FBAV\/(\d+)/,
    Firefox: /Firefox\/(\d+)/,
    'Firefox Mobile': /(?:Firefox|FxiOS)\/(\d+)/,
    HeadlessChrome: /HeadlessChrome\/(\d+)/,
    MiuiBrowser: /MiuiBrowser\/(\d+)/,
    'Mobile Safari': /Version\/(\d+)/,
    Opera: /OPR\/(\d+)/,
    'Opera Mobile': /(?:OPR|OPiOS)\/(\d+)/,
    Safari: /Version\/(\d+)/,
    'Samsung Internet': /SamsungBrowser\/(\d+)/,
    'Yandex Browser': /YaBrowser\/(\d+)/,
    Unknown: null
  };

  const pattern = versionPatterns[browser];
  if (pattern) {
    const match = ua.match(pattern);
    if (match && match[1]) return parseInt(match[1], 10);
  }

  return null;
};

const getReferrerDomain = (referrer: string): string => {
  if (!referrer) return '$direct';
  try {
    return new URL(referrer).hostname;
  } catch {
    return '$direct';
  }
};

const getStaticBrowserInfo = (): StaticBrowserInfo => {
  if (cachedStaticInfo) return cachedStaticInfo;

  const device = getDevice();

  cachedStaticInfo = {
    $os: getOS(),
    $browser: getBrowser(),
    $browser_version: getBrowserVersion(),
    ...(device ? { $device: device } : {}),
    $screen_height: window.screen.height,
    $screen_width: window.screen.width,
    $initial_referrer: document.referrer || '$direct',
    $initial_referring_domain: getReferrerDomain(document.referrer)
  };

  return cachedStaticInfo;
};

/**
 * Generate a unique insert ID for event deduplication
 */
const generateInsertId = (): string => {
  // Use crypto.randomUUID if available, fallback to timestamp + random
  if (crypto?.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
};

export const getBrowserProperties = (
  userId: string | null = null
): BrowserProperties => {
  const staticInfo = getStaticBrowserInfo();

  return {
    ...staticInfo,
    $current_url: window.location.href,
    time: Math.floor(Date.now() / 1000),
    $insert_id: generateInsertId(),
    distinct_id: userId || 'anonymous',
    $user_id: userId
  };
};
